import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  DeleteSweep as DeleteSweepIcon,
} from '@mui/icons-material';
import ExcelImport from '../../components/reporting/ExcelImport';
import { getSettings, updateSettings, recomputeTags } from '../../api/settings';
import { importJson } from '../../api/reporting';
import { idbClear, idbCount } from '../../utils/idbCache';
import { useTenant } from '../../contexts/TenantContext';

// Stores managed by the parcels-map and FPA features. Adding to this
// list is the only thing required to surface a new clear-button in the
// Cache tab.
const CACHE_STORES = [
  {
    key: 'parcels',
    label: 'Parcel polygons',
    description:
      'Cadastral GeoJSON cached per tenant boundary on the admin parcels map. 1-week TTL.',
  },
  {
    key: 'members',
    label: 'FPA member records',
    description:
      'Per-member records used to colour parcel labels by hazard. 24-hour TTL.',
  },
];

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ py: 3 }}>
    {value === index && children}
  </Box>
);

const Settings = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { tenantId, currentTenant } = useTenant();

  // Configuration state
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRecomputing, setIsRecomputing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // JSON import state
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);

  // Cache tab state — counts per store, refreshed on tab open and after
  // each clear. `clearing[store]` flags an in-flight clear so we can
  // disable the button.
  const [cacheCounts, setCacheCounts] = useState({});
  const [clearing, setClearing] = useState({});

  const refreshCacheCounts = async () => {
    const next = {};
    await Promise.all(
      CACHE_STORES.map(async (s) => {
        next[s.key] = await idbCount(s.key);
      }),
    );
    setCacheCounts(next);
  };

  // Refresh counts whenever the user opens the Cache tab.
  useEffect(() => {
    if (activeTab === 2) refreshCacheCounts();
  }, [activeTab]);

  const handleClearCache = async (storeKey) => {
    setClearing((prev) => ({ ...prev, [storeKey]: true }));
    try {
      await idbClear(storeKey);
      await refreshCacheCounts();
      setSuccessMessage(`Cleared ${storeKey} cache.`);
    } catch (err) {
      console.error(`Failed to clear ${storeKey} cache:`, err);
      setError(`Failed to clear ${storeKey} cache.`);
    } finally {
      setClearing((prev) => ({ ...prev, [storeKey]: false }));
    }
  };

  const handleClearAllCaches = async () => {
    setClearing((prev) => ({ ...prev, __all: true }));
    try {
      await Promise.all(CACHE_STORES.map((s) => idbClear(s.key)));
      await refreshCacheCounts();
      setSuccessMessage('All caches cleared.');
    } catch (err) {
      console.error('Failed to clear caches:', err);
      setError('Failed to clear caches.');
    } finally {
      setClearing((prev) => ({ ...prev, __all: false }));
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getSettings();
        setSettings(data);
      } catch (err) {
        console.error('Error fetching settings:', err);
        setError('Failed to load settings. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
    setSuccessMessage(null);
  };

  const handleSave = async () => {
    if (!settings) return;
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const payload = {
        fire_season_start_month: settings.fire_season_start_month,
        fire_season_end_month: settings.fire_season_end_month,
        fire_season_cross_year: settings.fire_season_cross_year,
        financial_year_start_month: settings.financial_year_start_month,
      };
      const data = await updateSettings(payload);
      setSettings(data);
      setSuccessMessage('Settings saved. New wildfires will use the updated bounds. Existing tags are not recomputed.');
    } catch (err) {
      console.error('Error saving settings:', err);
      if (err.response && err.response.data && err.response.data.detail) {
        setError(`Failed to save: ${JSON.stringify(err.response.data.detail)}`);
      } else {
        setError('Failed to save settings. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleRecompute = async () => {
    setIsRecomputing(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const data = await recomputeTags();
      const count = data?.updated_count ?? 0;
      setSuccessMessage(
        `Recomputed season tags — ${count} wildfire${count === 1 ? '' : 's'} updated.`
      );
    } catch (err) {
      console.error('Error recomputing tags:', err);
      if (err.response && err.response.data && err.response.data.detail) {
        setError(`Failed to recompute: ${JSON.stringify(err.response.data.detail)}`);
      } else {
        setError('Failed to recompute tags. Please try again.');
      }
    } finally {
      setIsRecomputing(false);
    }
  };

  // JSON import
  const handleFileSelect = (event) => setImportFile(event.target.files[0]);

  const handleJsonImport = async () => {
    if (!importFile) { setError('Please select a JSON file to import.'); return; }
    if (!tenantId) {
      setError('Please select a tenant from the tenant switcher before importing.');
      return;
    }
    setIsImporting(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('tenant_id', String(tenantId));
      const data = await importJson(formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const weatherSuffix = typeof data.weather_imported_count === 'number'
        ? `, Weather: ${data.weather_imported_count}`
        : '';
      setSuccessMessage(`Import completed! Imported: ${data.imported_count}, Skipped: ${data.skipped_count}, Total: ${data.total_processed}${weatherSuffix}`);
      setImportFile(null);
      const fi = document.getElementById('json-import-file');
      if (fi) fi.value = '';
    } catch (err) {
      setError(err.response?.data?.detail ? `Import failed: ${err.response.data.detail}` : 'Failed to import JSON data.');
    } finally {
      setIsImporting(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Settings
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 3 }}>{successMessage}</Alert>}

      <Paper elevation={2} sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Configuration" />
          <Tab label="Data Import" />
          <Tab label="Cache" />
        </Tabs>
      </Paper>

      {/* Configuration Tab */}
      <TabPanel value={activeTab} index={0}>
        {settings && (
          <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Typography variant="h6" gutterBottom>
              Fire Season
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Wildfires are tagged with the fire season they belong to. When cross-year mode is off, the tag is simply the calendar year (e.g. "2024"). When cross-year mode is on, the season spans the new-year boundary and the tag becomes e.g. "2024/2025".
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="fs-start-label">Start Month</InputLabel>
                  <Select
                    labelId="fs-start-label"
                    value={settings.fire_season_start_month}
                    label="Start Month"
                    onChange={(e) => handleChange('fire_season_start_month', e.target.value)}
                  >
                    {MONTHS.map((m) => (
                      <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="fs-end-label">End Month</InputLabel>
                  <Select
                    labelId="fs-end-label"
                    value={settings.fire_season_end_month}
                    label="End Month"
                    onChange={(e) => handleChange('fire_season_end_month', e.target.value)}
                  >
                    {MONTHS.map((m) => (
                      <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.fire_season_cross_year}
                      onChange={(e) => handleChange('fire_season_cross_year', e.target.checked)}
                    />
                  }
                  label="Fire season crosses the new-year boundary (e.g. October -> April)"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" gutterBottom>
              Financial Year
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Wildfires are tagged with the financial year they fall into. For example, with a start month of April, a wildfire on 15 Jan 2025 is tagged "2024/2025".
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="fy-start-label">Start Month</InputLabel>
                  <Select
                    labelId="fy-start-label"
                    value={settings.financial_year_start_month}
                    label="Start Month"
                    onChange={(e) => handleChange('financial_year_start_month', e.target.value)}
                  >
                    {MONTHS.map((m) => (
                      <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={handleRecompute}
                disabled={isRecomputing || isSaving}
              >
                {isRecomputing ? 'Recomputing...' : 'Recompute Tags'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isSaving || isRecomputing}
              >
                {isSaving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'right' }}>
              "Recompute Tags" re-applies the current bounds to every existing wildfire.
            </Typography>
          </Paper>
        )}
      </TabPanel>

      {/* Data Import Tab */}
      <TabPanel value={activeTab} index={1}>
        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Import Wildfire Data (JSON)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Import JSON data to populate the database with wildfire records.
            Records will be created under the currently selected tenant
            {currentTenant ? ` (${currentTenant.name})` : ' — none selected, pick one in the tenant switcher first'}.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <input id="json-import-file" type="file" accept=".json" onChange={handleFileSelect} style={{ display: 'none' }} />
            <label htmlFor="json-import-file">
              <Button variant="outlined" component="span" startIcon={<UploadIcon />}>Select JSON File</Button>
            </label>
            {importFile && <Typography variant="body2" sx={{ flexGrow: 1 }}>Selected: {importFile.name}</Typography>}
            <Button variant="contained" color="warning" startIcon={<UploadIcon />} onClick={handleJsonImport} disabled={!importFile || isImporting}>
              {isImporting ? 'Importing...' : 'Import JSON'}
            </Button>
          </Box>
        </Paper>

        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Typography variant="h6" gutterBottom>Import Historical Wildfire Data (Excel / GUFPA)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Import prior-year fire statistics from GUFPA monthly Excel files. Weather data will be estimated using Open-Meteo archive at the peak FDI hour for each incident date.
          </Typography>
          <ExcelImport />
        </Paper>
      </TabPanel>

      {/* Cache Tab — purges client-side IndexedDB stores. Useful when
          stale data is suspected (e.g. a member's hazards changed but
          the map still shows the old colour because the 24h TTL hasn't
          expired) or when freeing storage. Server-side data is never
          touched here. */}
      <TabPanel value={activeTab} index={2}>
        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Typography variant="h6" gutterBottom>Browser cache</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            These caches live in your browser and speed up the admin pages.
            Clearing them only affects this device — other users keep their
            own caches. Pages that use a cleared store will refetch on the
            next visit.
          </Typography>

          <Grid container spacing={2}>
            {CACHE_STORES.map((s) => (
              <Grid item xs={12} md={6} key={s.key}>
                <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {s.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                    {s.description}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 'auto' }}>
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {cacheCounts[s.key] ?? '—'} entr{(cacheCounts[s.key] ?? 0) === 1 ? 'y' : 'ies'}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={clearing[s.key] ? <CircularProgress size={14} /> : <DeleteSweepIcon />}
                      onClick={() => handleClearCache(s.key)}
                      disabled={Boolean(clearing[s.key]) || (cacheCounts[s.key] ?? 0) === 0}
                    >
                      Clear
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="warning"
              startIcon={clearing.__all ? <CircularProgress size={16} /> : <DeleteSweepIcon />}
              onClick={handleClearAllCaches}
              disabled={Boolean(clearing.__all)}
            >
              Clear all caches
            </Button>
          </Box>
        </Paper>
      </TabPanel>
    </Container>
  );
};

export default Settings;
